// api/tickets.js (o el archivo donde lo tengas)

import { authFetch } from "../utils/authFetch";
import { API_BASE_URL } from "../config";

/** URL base centralizada (cambia sola según el entorno; ver src/config.js). */
const BASE_API = API_BASE_URL;

/** Rutas relativas centralizadas */
const ENDPOINTS = {
  ticketsBySucursal: (sucursalId) => `/tickets/estado/0/sucursal/${sucursalId}`,
  membresiasBySucursal: (sucursalId) => `/ingreso_membresia/activos/sucursal-simple/${sucursalId}`,
  plazasBySucursal: (sucursalId) => `/plazas/${sucursalId}/sucursal`,
  ticketById: (ticketId) => `/tickets/${ticketId}`,
  tarifaMontoDetalle: (sucursalId, placa) => `/tarifaMonto/${sucursalId}/${encodeURIComponent(placa)}/detalle`,
  tickets: () => `/tickets`,
  tiposTarifaBySucursal: (sucursalId) => `/tarifa/sucursal/${sucursalId}/tipos-tarifa`,
  sucursalById: (sucursalId) => `/api/sucursales/${sucursalId}`,
  validarMembresia: (placa, sucursalId) =>
    `/membresia/validar/${encodeURIComponent(placa)}/sucursal/${sucursalId}`,
  crearIngresoMembresia: () => `/ingreso_membresia/crear`,
};

/** Evita “números mágicos” */
const TicketEstado = Object.freeze({
  CANCELADO: 1,
});

/** Construye URL absoluta de forma segura */
function buildUrl(path) {
  return new URL(path, BASE_API).toString();
}

/** Intenta parsear JSON sin romper si viene vacío o texto plano */
async function safeJson(res) {
  const text = await res.text();
  if (!text) return null;
  try { return JSON.parse(text); } catch { return text; }
}

/** Lanza error enriquecido si la respuesta no es ok */
async function ensureOk(res, fallbackMsg) {
  if (res.ok) return res;
  const body = await safeJson(res);
  const detail = typeof body === "string" ? body : JSON.stringify(body);
  const err = new Error(`${fallbackMsg} (HTTP ${res.status} ${res.statusText})${detail ? `: ${detail}` : ""}`);
  err.status = res.status;
  err.body = body;
  throw err;
}

/** Timeout simple con AbortController (opcional pero útil) */
function withTimeout(ms = 15000, signal) {
  if (!ms && !signal) return undefined;
  const ctrl = new AbortController();
  const timer = ms ? setTimeout(() => ctrl.abort("timeout"), ms) : null;

  if (signal) {
    signal.addEventListener("abort", () => ctrl.abort(signal.reason), { once: true });
  }
  return { signal: ctrl.signal, cleanup: () => timer && clearTimeout(timer) };
}

/** Helper GET JSON */
async function getJSON(path, { timeoutMs = 15000, signal } = {}) {
  const t = withTimeout(timeoutMs, signal);
  try {
    const res = await authFetch(buildUrl(path), { method: "GET", signal: t?.signal });
    await ensureOk(res, "Error en la consulta");
    return res.json();
  } finally {
    t?.cleanup?.();
  }
}

/** Helper PUT JSON */
async function putJSON(path, payload, { timeoutMs = 15000, signal } = {}) {
  const t = withTimeout(timeoutMs, signal);
  try {
    const res = await authFetch(buildUrl(path), {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
      signal: t?.signal,
    });
    await ensureOk(res, "Error en la actualización");
    return await safeJson(res); // puede ser null si el backend no devuelve cuerpo
  } finally {
    t?.cleanup?.();
  }
}

/** Helper POST JSON */
async function postJSON(path, payload, { timeoutMs = 15000, signal } = {}) {
  const t = withTimeout(timeoutMs, signal);
  try {
    const res = await authFetch(buildUrl(path), {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
      signal: t?.signal,
    });
    await ensureOk(res, "Error al crear el registro");
    return await safeJson(res);
  } finally {
    t?.cleanup?.();
  }
}

/** Valida que el ID sea entero positivo */
function asIntId(value, name = "id") {
  const n = Number.parseInt(String(value), 10);
  if (!Number.isFinite(n) || n <= 0) {
    throw new Error(`Parámetro inválido: ${name} debe ser un entero positivo.`);
  }
  return n;
}

/**
 * Obtiene los tickets por sucursalId.
 */
export const getTickets = async (idsucursal, opts) => {
  const id = asIntId(idsucursal, "sucursalId");
  return getJSON(ENDPOINTS.ticketsBySucursal(id), opts);
};

/**
 * Obtiene los ingresos de membresía por sucursalId.
 */
export const getMembresiasBySucursal = async (idsucursal, opts) => {
  const id = asIntId(idsucursal, "sucursalId");
  return getJSON(ENDPOINTS.membresiasBySucursal(id), opts);
};

/**
 * Obtiene las plazas por sucursalId.
 */
export const getPlazasBySucursal = async (idsucursal, opts) => {
  const id = asIntId(idsucursal, "sucursalId");
  return getJSON(ENDPOINTS.plazasBySucursal(id), opts);
};

/**
 * Cancela un ticket enviando una actualización de estado y el empleado de cierre.
 * Mantengo tu lectura de localStorage para compatibilidad.
 * Si puedes, pásalo como parámetro desde tu capa de sesión.
 */
export const cancelTicket = async (ticketId, { signal, timeoutMs, empleadoId } = {}) => {
  const id = asIntId(ticketId, "ticketId");

  const empleado =
    empleadoId ??
    (typeof window !== "undefined" ? window.localStorage.getItem("idEmpleado") : null);

  const empleadoIdNum = asIntId(empleado, "empleadoId");

  const payload = {
    empleadoCierre: { id: empleadoIdNum },
    mfinal: 0, // si esto cambia por negocio, vuelve param
    estado: TicketEstado.CANCELADO,
  };

  await putJSON(ENDPOINTS.ticketById(id), payload, { signal, timeoutMs });
  return true;
};

/**
 * Paso 1 del cobro: pide al backend el detalle con el monto a cobrar para una placa.
 * El backend calcula el monto según hora de entrada vs. ahora y la tarifa configurada.
 * Devuelve { id, placa, hentrada, hsalida, monto }.
 */
export const getTarifaMontoDetalle = async (placa, idsucursal, opts) => {
  const id = asIntId(idsucursal, "sucursalId");
  const p = String(placa || "").trim();
  if (!p) throw new Error("La placa es requerida.");
  return getJSON(ENDPOINTS.tarifaMontoDetalle(id, p), opts);
};

/**
 * Paso 2 del cobro: cierra/paga el ticket.
 * PUT /tickets/{id} con { mfinal, estado: 1, tpago, empleadoCierre: { id } }.
 * tpago: "E" (efectivo) | "T" (transferencia) | "TC" (tarjeta).
 * El empleado de cierre va como objeto anidado, no plano.
 */
export const cobrarTicket = async (ticketId, { mfinal, tpago, empleadoId, signal, timeoutMs } = {}) => {
  const id = asIntId(ticketId, "ticketId");

  if (!["E", "T", "TC"].includes(tpago)) {
    throw new Error("Método de pago inválido (use E, T o TC).");
  }
  const monto = Number(mfinal);
  if (!Number.isFinite(monto) || monto < 0) {
    throw new Error("Monto inválido.");
  }

  const empleado =
    empleadoId ??
    (typeof window !== "undefined" ? window.localStorage.getItem("idEmpleado") : null);
  const empleadoIdNum = asIntId(empleado, "empleadoId");

  const payload = {
    mfinal: monto,
    estado: 1, // cerrado / pagado
    tpago,
    empleadoCierre: { id: empleadoIdNum },
  };

  await putJSON(ENDPOINTS.ticketById(id), payload, { signal, timeoutMs });
  return true;
};

// =====================================================================
//  Registro de ingresos + datos para imprimir el ticket
//  Réplica de lo que hace la app móvil en ImpresionScreen.kt.
// =====================================================================

/** Cabecera del ticket por sucursal; cambia muy poco, así que se cachea. */
const cacheSucursal = new Map();

/**
 * Datos de cabecera del ticket: { nombre, ubicacion, descripcionTicket }.
 * El móvil los saca de /api/empleados/{id}.sucursal; acá se piden
 * directo por sucursal, que es lo que el web ya tiene en localStorage.
 */
export const getSucursalTicketInfo = async (idsucursal, opts) => {
  const id = asIntId(idsucursal, "sucursalId");
  if (cacheSucursal.has(id)) return cacheSucursal.get(id);

  const sucursal = await getJSON(ENDPOINTS.sucursalById(id), opts);
  const info = {
    nombre: sucursal?.nombre ?? "",
    ubicacion: sucursal?.ubicacion ?? "",
    descripcionTicket: sucursal?.descripcionTicket ?? "",
  };
  cacheSucursal.set(id, info);
  return info;
};

/** Tipos de tarifa configurados para la sucursal (los mismos que ve el móvil). */
export const getTiposTarifaBySucursal = async (idsucursal, opts) => {
  const id = asIntId(idsucursal, "sucursalId");
  const tipos = await getJSON(ENDPOINTS.tiposTarifaBySucursal(id), opts);
  return Array.isArray(tipos) ? [...tipos].sort((a, b) => a.id - b.id) : [];
};

/**
 * Crea el registro de ingreso (ticket normal).
 * Body igual al del móvil (postTicket): { placa, empleado, estado, idTipoTarifa }.
 */
export const crearTicketIngreso = async (
  { placa, idTipoTarifa, empleadoId, signal, timeoutMs } = {}
) => {
  const p = String(placa || "").trim();
  if (!p) throw new Error("La placa es requerida.");
  const idTipo = asIntId(idTipoTarifa, "idTipoTarifa");

  const empleado =
    empleadoId ??
    (typeof window !== "undefined" ? window.localStorage.getItem("idEmpleado") : null);
  const empleadoIdNum = asIntId(empleado, "empleadoId");

  const payload = {
    placa: p,
    empleado: { id: empleadoIdNum },
    estado: 0,
    idTipoTarifa: idTipo,
  };

  return postJSON(ENDPOINTS.tickets(), payload, { signal, timeoutMs });
};

/**
 * ¿La placa tiene membresía en esta sucursal?
 * Devuelve { valida, cliente, idMembresia, ... }, o null si no hay
 * membresía alguna (el backend responde !2xx en ese caso, igual que
 * en el móvil, donde eso significa "flujo de ticket normal").
 */
export const validarMembresia = async (placa, idsucursal, { timeoutMs = 15000, signal } = {}) => {
  const id = asIntId(idsucursal, "sucursalId");
  const p = String(placa || "").trim();
  if (!p) throw new Error("La placa es requerida.");

  const t = withTimeout(timeoutMs, signal);
  try {
    const res = await authFetch(buildUrl(ENDPOINTS.validarMembresia(p, id)), {
      method: "GET",
      signal: t?.signal,
    });
    if (!res?.ok) return null;
    const body = await safeJson(res);
    return body && typeof body === "object" ? body : null;
  } catch {
    return null; // sin membresía / error de red -> se sigue con ticket normal
  } finally {
    t?.cleanup?.();
  }
};

/** yyyy-MM-ddTHH:mm:ss en hora local, el formato que espera el backend. */
function fechaHoraLocalISO(fecha = new Date()) {
  const dd = (n) => String(n).padStart(2, "0");
  return (
    `${fecha.getFullYear()}-${dd(fecha.getMonth() + 1)}-${dd(fecha.getDate())}` +
    `T${dd(fecha.getHours())}:${dd(fecha.getMinutes())}:${dd(fecha.getSeconds())}`
  );
}

/** Registra el ingreso de un vehículo con membresía activa. */
export const crearIngresoMembresia = async (idMembresia, { signal, timeoutMs } = {}) => {
  const id = asIntId(idMembresia, "idMembresia");
  const payload = {
    membresia: { id },
    fechaIngreso: fechaHoraLocalISO(),
    estado: 0,
  };
  return postJSON(ENDPOINTS.crearIngresoMembresia(), payload, { signal, timeoutMs });
};

/**
 * Hora oficial de entrada según el servidor (la pone la BD, no el cliente).
 * Igual que fetchServerStampForPlaca del móvil: relee los tickets abiertos
 * de la sucursal y toma el más reciente de esa placa. Devuelve null si no
 * se pudo obtener, para que el llamador caiga a la hora local.
 */
export const getHentradaServidor = async (placa, idsucursal, opts) => {
  try {
    const p = String(placa || "").trim().toUpperCase();
    const tickets = (await getTickets(idsucursal, opts)) || [];
    const propios = tickets.filter(
      (t) => String(t?.placa || "").toUpperCase() === p && t?.hentrada
    );
    if (propios.length === 0) return null;
    return propios.reduce((masReciente, t) =>
      new Date(t.hentrada) > new Date(masReciente.hentrada) ? t : masReciente
    ).hentrada;
  } catch {
    return null;
  }
};

/**
 * Fecha de ingreso registrada para una membresía recién marcada.
 * Mismo criterio que getHentradaServidor pero sobre los ingresos activos.
 */
export const getFechaIngresoMembresiaServidor = async (placa, idsucursal, opts) => {
  try {
    const p = String(placa || "").trim().toUpperCase();
    const activos = (await getMembresiasBySucursal(idsucursal, opts)) || [];
    const propios = activos.filter(
      (m) => String(m?.placa || "").toUpperCase() === p && m?.fechaIngreso
    );
    if (propios.length === 0) return null;
    return propios.reduce((masReciente, m) =>
      new Date(m.fechaIngreso) > new Date(masReciente.fechaIngreso) ? m : masReciente
    ).fechaIngreso;
  } catch {
    return null;
  }
};
